import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Translate, Sparkle, FloppyDisk, Trash, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGUAGES, LANG_BY_CODE } from "@/lib/languages";

const PICKABLE = LANGUAGES.filter((l) => l.code !== "en");
const FIELDS = {
  articles: [["title", "Title", false], ["summary", "Summary", false], ["body", "Body", true]],
  news: [["title", "Headline", false], ["summary", "Summary", false]],
};

// Per-item multilanguage editor: AI-draft, edit or paste a translation, save/remove. Used in the CMS.
export default function ContentTranslations({ kind, item }) {
  const [open, setOpen] = useState(false);
  const [translations, setTranslations] = useState(item.translations || {});
  const [lang, setLang] = useState("");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const langs = Object.keys(translations);
  const fields = FIELDS[kind];

  const selectLang = (code) => {
    setLang(code);
    setDraft(translations[code] || {});
  };
  const aiDraft = async () => {
    if (!lang) return toast.error("Pick a language first");
    setBusy("ai");
    try {
      const { data } = await api.post(`/admin/content/${kind}/${item.id}/translate`, { lang });
      setDraft(data.fields || {});
      setTranslations((t) => ({ ...t, [lang]: data.fields || {} }));
      toast.success(`AI drafted ${LANG_BY_CODE[lang]?.label} — review & save`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI draft failed"); }
    finally { setBusy(""); }
  };
  const save = async () => {
    if (!lang) return;
    setBusy("save");
    try {
      await api.put(`/admin/content/${kind}/${item.id}/translation`, { lang, fields: draft });
      setTranslations((t) => ({ ...t, [lang]: draft }));
      toast.success(`${LANG_BY_CODE[lang]?.label} translation saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(""); }
  };
  const remove = async (code) => {
    setBusy("del");
    try {
      await api.delete(`/admin/content/${kind}/${item.id}/translation/${code}`);
      setTranslations((t) => { const n = { ...t }; delete n[code]; return n; });
      if (lang === code) { setLang(""); setDraft({}); }
      toast.success("Translation removed");
    } catch { toast.error("Could not remove"); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-2 border-t border-border/50 pt-2" data-testid={`content-translations-${item.id}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`translations-toggle-${item.id}`}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-secondary">
        {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
        <Translate size={14} /> Translations {langs.length > 0 && <span className="text-secondary">({langs.length})</span>}
      </button>

      {open && (
        <div className="mt-3 space-y-3 rounded-xl bg-muted/30 p-3">
          {langs.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {langs.map((c) => (
                <button key={c} onClick={() => selectLang(c)} data-testid={`translation-chip-${item.id}-${c}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${lang === c ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>
                  {LANG_BY_CODE[c]?.native || c}
                  <Trash size={11} onClick={(e) => { e.stopPropagation(); remove(c); }} className="ml-0.5 hover:text-red-500" />
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px]">
              <Label className="text-xs">Language</Label>
              <Select value={lang} onValueChange={selectLang}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid={`translation-lang-${item.id}`}><SelectValue placeholder="Pick a language…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {PICKABLE.map((l) => <SelectItem key={l.code} value={l.code}>{l.native} · {l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" disabled={!lang || busy === "ai"} onClick={aiDraft} data-testid={`translation-ai-${item.id}`}>
              <Sparkle size={14} weight="fill" /> {busy === "ai" ? "Drafting…" : "AI draft"}
            </Button>
          </div>

          {lang && (
            <div className="space-y-2.5">
              {fields.map(([key, label, isArea]) => (
                <div key={key}>
                  <Label className="text-xs">{label} <span className="text-muted-foreground">· {LANG_BY_CODE[lang]?.label}</span></Label>
                  {isArea
                    ? <Textarea rows={6} className="mt-1 text-sm" value={draft[key] || ""} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} placeholder="AI-draft, type, or paste the translated content…" data-testid={`translation-field-${item.id}-${key}`} />
                    : <Input className="mt-1 text-sm" value={draft[key] || ""} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} data-testid={`translation-field-${item.id}-${key}`} />}
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Button size="sm" className="rounded-full gap-1.5" disabled={busy === "save"} onClick={save} data-testid={`translation-save-${item.id}`}>
                  <FloppyDisk size={14} /> {busy === "save" ? "Saving…" : "Save translation"}
                </Button>
                {translations[lang] && <Badge variant="outline" className="rounded-full text-[11px] font-normal">Live in {LANG_BY_CODE[lang]?.native}</Badge>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
